import { useContext, useEffect, useMemo, useRef, useCallback } from "react";
import { PROMO_OBJECT } from "../../../pages/promotions/constants";
import { PropertyStoreContext } from "../../../contexts/PropertyStoreContext";
import {
  offerTypes,
  allCalculationTypes,
  limitedCalculationType,
  TOTAL_PRICE_CALCULATION_TYPE,
  discountTypes,
  DAYS,
  dayMap,
  MAX_FIXED_DISCOUNT,
  MAX_FIXED_DAY_DISCOUNT,
} from "../OfferType/offerTypeConstants";
import { validateDiscountAmounts } from "../utils/discountValidation";
import { D_EDGE_CRS_ID } from "../../../constants";

/**
 * Sub-hook of `useOfferTypeState` owning discount / offer-type state,
 * handlers, and the next-button-disabled effect.
 *
 * Exposes the same field names the parent orchestrator used to expose
 * directly so existing consumers continue to work unchanged.
 */
export function useOfferTypeDiscount({
  promo,
  onFieldChange,
  setIsNextBtnDisabled,
  calculationType,
  setCalculationType,
  // CRS of the header-selected property. D-Edge only supports discounts on
  // the total price, so the calculation method is locked whenever the promo
  // applies to such a property — see isCalculationMethodLocked, which follows
  // the whole Apply To selection and falls back to this prop before anything
  // is selected.
  crsId,
  // Outputs of useOfferTypePropertySelection, used to resolve each selected
  // property's CRS for the calculation-method lock.
  selectedProperties = [],
  propertyOptions = [],
}) {
  const { selectedProperty, properties } = useContext(PropertyStoreContext);

  // Memoized promo data with defaults
  const promoData = useMemo(() => {
    const incoming = promo || {};
    return {
      ...PROMO_OBJECT,
      ...incoming,
      rateRule: {
        ...PROMO_OBJECT.rateRule,
        ...incoming.rateRule,
        ruleVO: {
          ...PROMO_OBJECT.rateRule.ruleVO,
          ...incoming.rateRule?.ruleVO,
          discountVO: Array.isArray(incoming.rateRule?.ruleVO?.discountVO)
            ? incoming.rateRule.ruleVO.discountVO
            : {
                ...PROMO_OBJECT.rateRule.ruleVO.discountVO,
                ...incoming.rateRule?.ruleVO?.discountVO,
              },
        },
      },
    };
  }, [promo]);

  // Refs
  const prevDiscountValueRef = useRef(null);
  const discountVORef = useRef(
    promo?.rateRule?.ruleVO?.discountVO ||
      PROMO_OBJECT.rateRule.ruleVO.discountVO
  );
  const discountVOKeyRef = useRef("");

  // propertyId -> crsId for every property we can account for: the catalog page
  // held by the context, everything the picker has searched or seeded, and the
  // header selection (whose CRS arrives as the `crsId` prop — the context object
  // is not guaranteed to carry one). Ids are stringified and CRS values are
  // numbered because both round-trip through onFieldChange and Number()
  // coercion elsewhere, and a type mismatch here would silently *unlock*.
  // A blank CRS never overwrites a known one — the header entry is written last
  // and its prop is optional (the campaign flow omits it entirely).
  const crsIdByPropertyId = useMemo(() => {
    const byId = new Map();
    const remember = (id, propertyCrsId) => {
      if (id === undefined || id === null) return;
      if (
        propertyCrsId === undefined ||
        propertyCrsId === null ||
        propertyCrsId === ""
      ) {
        return;
      }
      const numericCrsId = Number(propertyCrsId);
      if (!Number.isFinite(numericCrsId)) return;
      byId.set(String(id), numericCrsId);
    };
    (properties || []).forEach((p) => remember(p.propertyId, p.crsId));
    propertyOptions.forEach((option) => remember(option.value, option.crsId));
    remember(selectedProperty?.propertyId, crsId);
    return byId;
  }, [properties, propertyOptions, selectedProperty?.propertyId, crsId]);

  // Calculation method is locked to "total price" when any property the promo
  // applies to runs on a CRS that cannot discount anything else (D-Edge). The
  // Apply To selection changes while the drawer is open, so this is derived on
  // every render rather than stored — deselecting the last D-Edge property
  // widens the list again (the already-chosen method is left as it is).
  //
  // A property beyond the loaded catalog page that was never searched cannot be
  // resolved (there is no property-by-id endpoint, only paginated search) and so
  // contributes nothing here. Only reachable in edit mode, where the saved promo
  // supplies the ids; at create time every selectable property came through the
  // picker. Same limitation as the note in useRoomsRateplansDrawerHandlers.
  //
  // GC-created promos are exempt: the whole form is read-only for them (see
  // isSourceReadOnly in CreatePromo), and it is there to show what GC actually
  // configured — restricting the options would blank out, or misreport, a
  // method the supplier cannot change anyway.
  const isGcCreated = promo?._derivedRateMeta?.isGcCreated === true;
  const appliesToDEdgeProperty = selectedProperties.length
    ? selectedProperties.some(
        (id) => crsIdByPropertyId.get(String(id)) === D_EDGE_CRS_ID
      )
    : Number(crsId) === D_EDGE_CRS_ID;
  const isCalculationMethodLocked = appliesToDEdgeProperty && !isGcCreated;
  const calculationOptions = isCalculationMethodLocked
    ? limitedCalculationType
    : allCalculationTypes;

  // Get discount value
  const getDiscountValue = useMemo(() => {
    const {
      mCompDiscountType,
      discountType,
      percentDiscountMap,
      fixedDiscountMap,
      percentDiscountAmount,
      fixedDiscountAmount,
    } = promoData.rateRule.ruleVO.discountVO;

    if (mCompDiscountType === offerTypes[0].value) {
      if (discountType === discountTypes[1].value) {
        return Object.values(percentDiscountMap).reduce(
          (total, discount) => total + discount,
          0
        );
      }
      return percentDiscountAmount ?? 0;
    }

    if (discountType === discountTypes[1].value) {
      return Object.values(fixedDiscountMap).reduce(
        (total, discount) => total + discount,
        0
      );
    }
    return fixedDiscountAmount ?? 0;
  }, [promoData]);

  /**
   * Warning shown under the Amount field while the promo holds a percentage
   * above the cap.
   *
   * Derived from the promo rather than latched when a keystroke is refused: an
   * over-limit entry is now kept in the field (see handleDiscountChange), so
   * the warning has to describe what the form actually holds. That also keeps
   * it in step with the footer, which disables Launch/Save off the very same
   * check, and covers the cases a keystroke never sees — a day left over the
   * cap while another day is edited, or a pre-cap promo reopened for edit.
   */
  const discountLimitError = useMemo(
    () => validateDiscountAmounts(promoData)[0] ?? null,
    [promoData]
  );

  // Handlers
  const resetOfferTypeFields = useCallback(() => {
    onFieldChange("rateRule.ruleVO.discountVO.fixedDiscountAmount", 0);
    onFieldChange("rateRule.ruleVO.discountVO.percentDiscountAmount", 0);

    DAYS.forEach((day) => {
      onFieldChange(
        `rateRule.ruleVO.discountVO.percentDiscountMap.${dayMap.get(day)}`,
        0
      );
      onFieldChange(
        `rateRule.ruleVO.discountVO.fixedDiscountMap.${dayMap.get(day)}`,
        0
      );
    });
  }, [onFieldChange]);

  const handlePriceAdjustmentChange = useCallback(
    (value) => {
      onFieldChange(
        "rateRule.ruleVO.discountVO.priceAdjustment",
        value || "DECREASE"
      );
    },
    [onFieldChange]
  );

  const handleChange = useCallback(
    (newValue) => {
      onFieldChange(
        "rateRule.ruleVO.discountVO.mCompDiscountType",
        newValue || 2
      );
      resetOfferTypeFields();
    },
    [onFieldChange, resetOfferTypeFields]
  );

  const handleDiscountTypeChange = useCallback(
    (value) => {
      onFieldChange("rateRule.ruleVO.discountVO.discountType", value || 4);
      resetOfferTypeFields();
    },
    [onFieldChange, resetOfferTypeFields]
  );

  const handleCalculationTypeChange = useCallback(
    (value, index = 0) => {
      setCalculationType(value);

      // Stacked (GC-created) promos carry an array of discountVO entries.
      // When changing one entry's calculationType, update the entire array (EO-486).
      const isStacked = Array.isArray(promoData.rateRule.ruleVO.discountVO);

      if (isStacked) {
        const updatedArray = promoData.rateRule.ruleVO.discountVO.map(
          (entry, i) => {
            if (i === index) {
              return { ...entry, calculationType: value };
            }
            return entry;
          }
        );
        onFieldChange("rateRule.ruleVO.discountVO", updatedArray);
      } else {
        // Single discountVO: persist via appliedOnType for backward compatibility
        onFieldChange("rateRule.ruleVO.discountVO.appliedOnType", value);
      }
    },
    [setCalculationType, onFieldChange, promoData.rateRule.ruleVO.discountVO]
  );

  // Use promoData (current state) instead of discountVO (stale ref)
  const isPercentOffer =
    promoData.rateRule.ruleVO.discountVO.mCompDiscountType ===
    offerTypes[0].value;

  const handleDiscountChange = useCallback(
    (value) => {
      if (value === "" || value === null || value === undefined) {
        value = "0";
      }

      const stringValue = String(value);

      if (stringValue.split(".")[1]?.length > 2) {
        return;
      }
      const newValue = stringValue.replace(/^0+(?=\d)/, "");

      // A percentage past the cap is stored, not refused. Reverting the
      // keystroke left the field showing the last good value (99) while the
      // warning complained about a number the user could no longer see, and
      // the promo was then created at 99. The entry stands, discountLimitError
      // explains it, and the footer stays disabled until it is brought back
      // under the cap.
      if (newValue >= 0 && newValue <= MAX_FIXED_DISCOUNT) {
        onFieldChange(
          isPercentOffer
            ? "rateRule.ruleVO.discountVO.percentDiscountAmount"
            : "rateRule.ruleVO.discountVO.fixedDiscountAmount",
          newValue
        );
      }
    },
    [onFieldChange, isPercentOffer]
  );

  const handleDayChange = useCallback(
    (value, day) => {
      if (value === "" || value === null || value === undefined) {
        value = "0";
      }

      const stringValue = String(value);

      if (stringValue.split(".")[1]?.length > 2) {
        return;
      }

      // Same cap as the all-days amount, and kept the same way: a per-day
      // percentage over 100 is stored and flagged rather than swallowed, so a
      // day set to 150% is visible instead of silently staying at its previous
      // value while the promo is submitted.
      if (stringValue >= 0 && stringValue <= MAX_FIXED_DAY_DISCOUNT) {
        const newValue = stringValue.replace(/^0+(?=\d)/, "");
        onFieldChange(
          isPercentOffer
            ? `rateRule.ruleVO.discountVO.percentDiscountMap.${dayMap.get(day)}`
            : `rateRule.ruleVO.discountVO.fixedDiscountMap.${dayMap.get(day)}`,
          newValue
        );
      }
    },
    [isPercentOffer, onFieldChange]
  );

  const handleKeyDown = useCallback((e, currentDay) => {
    const currentIndex = DAYS.indexOf(currentDay);

    if (e.key === "ArrowRight") {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % DAYS.length;
      const nextDay = DAYS[nextIndex];
      document.getElementById(`day-${nextDay}`).focus();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      const prevIndex = (currentIndex - 1 + DAYS.length) % DAYS.length;
      const prevDay = DAYS[prevIndex];
      document.getElementById(`day-${prevDay}`).focus();
    }
  }, []);

  // Effects

  // Snap the selected calculation method back to "total price" whenever the
  // property's CRS does not allow anything else (e.g. switching to a D-Edge
  // property, or loading a promo saved with another method).
  useEffect(() => {
    if (
      isCalculationMethodLocked &&
      Number(calculationType) !== TOTAL_PRICE_CALCULATION_TYPE
    ) {
      setCalculationType(TOTAL_PRICE_CALCULATION_TYPE);
    }
  }, [isCalculationMethodLocked, calculationType, setCalculationType]);

  // Update discountVO ref
  useEffect(() => {
    const currentDiscountVO =
      promo?.rateRule?.ruleVO?.discountVO ||
      PROMO_OBJECT.rateRule.ruleVO.discountVO;

    const discountVOKey = JSON.stringify({
      mCompDiscountType: currentDiscountVO?.mCompDiscountType,
      discountType: currentDiscountVO?.discountType,
      percentDiscountAmount: currentDiscountVO?.percentDiscountAmount,
      fixedDiscountAmount: currentDiscountVO?.fixedDiscountAmount,
      percentDiscountMap: currentDiscountVO?.percentDiscountMap,
      fixedDiscountMap: currentDiscountVO?.fixedDiscountMap,
    });

    if (discountVOKeyRef.current !== discountVOKey) {
      discountVOKeyRef.current = discountVOKey;
      discountVORef.current = currentDiscountVO;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    promo?.rateRule?.ruleVO?.discountVO?.mCompDiscountType,
    promo?.rateRule?.ruleVO?.discountVO?.discountType,
    promo?.rateRule?.ruleVO?.discountVO?.percentDiscountAmount,
    promo?.rateRule?.ruleVO?.discountVO?.fixedDiscountAmount,
  ]);

  // Update next button disabled state
  useEffect(() => {
    if (getDiscountValue !== prevDiscountValueRef.current) {
      prevDiscountValueRef.current = getDiscountValue;
      if (!getDiscountValue || getDiscountValue < 0) {
        setIsNextBtnDisabled(true);
      } else {
        setIsNextBtnDisabled(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDiscountValue]);

  return {
    // State
    promoData,
    calculationOptions,
    isCalculationMethodLocked,

    // Computed values
    getDiscountValue,
    discountLimitError,

    // Handlers
    resetOfferTypeFields,
    handlePriceAdjustmentChange,
    handleChange,
    handleDiscountTypeChange,
    handleCalculationTypeChange,
    handleDiscountChange,
    handleDayChange,
    handleKeyDown,
  };
}
